import moment from "moment";
import {
  BeamCamera,
  BeamCameraAlert,
  BeamDetection,
  BeamSensorDevice,
  BeamSensorReading,
} from "./types";

/**
 * Stand-in cameras and sensors for a page that has neither.
 *
 * Both screens are driven by hardware: a camera reaches the app only when a
 * cam device publishes to a page, and a reading only when a sensor is actually
 * strapped to someone. Until that hardware is provisioned the views have
 * nothing to draw, which leaves the design impossible to review — the empty
 * state is the only thing anyone can see.
 *
 * So a page with no real device of a kind falls back to this data, and says so
 * on screen. The badge is not decoration: demo readings and real vitals look
 * identical once they are in a tile, and a reader who cannot tell them apart
 * would be right to distrust both. The moment a real device publishes, the
 * fallback stops — demo data is never mixed in beside real data.
 */

/** How often the demo feeds move, matching a modest sensor's publish rate. */
export const DEMO_TICK_MS = 2000;

/**
 * A still frame drawn rather than fetched. A remote placeholder image would
 * make the demo depend on a network the reviewer may not have, and an empty
 * tile would hide the very framing the grid is being reviewed for.
 */
const demoFrame = (hue: number, label: string): string => {
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="270">',
    '<defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">',
    '<stop offset="0" stop-color="hsl(' + hue + ',32%,22%)"/>',
    '<stop offset="1" stop-color="hsl(' + hue + ',38%,9%)"/>',
    "</linearGradient></defs>",
    '<rect width="480" height="270" fill="url(#g)"/>',
    '<g stroke="hsl(' + hue + ',40%,55%)" stroke-opacity="0.16" stroke-width="1">',
    '<path d="M0 176 L480 140"/><path d="M0 214 L480 196"/>',
    '<path d="M96 270 L168 132"/><path d="M312 270 L268 132"/></g>',
    '<rect x="0" y="132" width="480" height="138" fill="#000" fill-opacity="0.18"/>',
    '<text x="16" y="30" fill="#ffffff" fill-opacity="0.45" ',
    'font-family="monospace" font-size="15">' + label + "</text>",
    "</svg>",
  ].join("");
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
};

/* ---------------- Cameras ---------------- */

export const DEMO_CAMERAS: BeamCamera[] = [
  {
    id: "demo-cam-front-gate",
    name: "Front Gate",
    location: "Main entrance · North",
    deviceKey: "demo-cam-front-gate",
    streamName: "demo-front-gate",
    snapshotUrl: demoFrame(210, "CAM 01  FRONT GATE"),
    addedAt: moment().subtract(9, "days").toISOString(),
  },
  {
    id: "demo-cam-loading-bay",
    name: "Loading Bay",
    location: "Rear yard · Dock 3",
    deviceKey: "demo-cam-loading-bay",
    streamName: "demo-loading-bay",
    snapshotUrl: demoFrame(24, "CAM 02  LOADING BAY"),
    addedAt: moment().subtract(9, "days").toISOString(),
  },
  {
    id: "demo-cam-lobby",
    name: "Lobby",
    location: "Reception desk",
    deviceKey: "demo-cam-lobby",
    streamName: "demo-lobby",
    snapshotUrl: demoFrame(168, "CAM 03  LOBBY"),
    addedAt: moment().subtract(6, "days").toISOString(),
  },
  {
    id: "demo-cam-parking",
    name: "Parking Deck",
    location: "Level B2 · Ramp",
    deviceKey: "demo-cam-parking",
    streamName: "demo-parking",
    snapshotUrl: demoFrame(266, "CAM 04  PARKING B2"),
    addedAt: moment().subtract(6, "days").toISOString(),
  },
  {
    id: "demo-cam-perimeter",
    name: "West Perimeter",
    location: "Fence line · Pole 7",
    deviceKey: "demo-cam-perimeter",
    streamName: "demo-perimeter",
    snapshotUrl: demoFrame(140, "CAM 05  W PERIMETER"),
    addedAt: moment().subtract(2, "days").toISOString(),
  },
];

/**
 * Built per mount rather than at import, so the ages read as minutes from the
 * moment the panel is opened instead of counting up from whenever the tab was
 * first loaded.
 */
export const demoCameraAlerts = (): BeamCameraAlert[] => [
  {
    id: "demo-alert-intrusion",
    cameraId: "demo-cam-loading-bay",
    severity: "critical",
    type: "Intrusion",
    description: "Person detected in a restricted zone after hours.",
    occurredAt: moment().subtract(2, "minutes").toISOString(),
  },
  {
    id: "demo-alert-loitering",
    cameraId: "demo-cam-front-gate",
    severity: "warning",
    type: "Loitering",
    description: "Vehicle stationary at the gate for over 6 minutes.",
    occurredAt: moment().subtract(11, "minutes").toISOString(),
  },
  {
    id: "demo-alert-crowd",
    cameraId: "demo-cam-lobby",
    severity: "info",
    type: "Crowding",
    description: "Occupancy above the configured threshold at reception.",
    occurredAt: moment().subtract(24, "minutes").toISOString(),
  },
];

/**
 * A smooth wander rather than a random one. Successive random values would
 * make the sparklines a fence of noise; a sum of two slow waves moves the way
 * a real reading does and stays reproducible for a given tick.
 */
const wobble = (seed: number, tick: number, amplitude: number): number =>
  Math.sin((tick + seed) / 2.3) * amplitude +
  Math.sin((tick + seed) / 7.7) * amplitude * 0.45;

/**
 * Detections carry geometry here. The real inference service publishes counts
 * without coordinates today, which leaves the overlay with nothing to draw —
 * so a demo built from counts alone would review the one part of this screen
 * that already works and hide the part nobody has seen.
 */
const DEMO_DETECTIONS: Record<
  string,
  { label: string; count: number; box: [number, number, number, number] }[]
> = {
  "demo-cam-loading-bay": [
    { label: "People", count: 1, box: [0.38, 0.34, 0.15, 0.46] },
    { label: "Vehicles", count: 2, box: [0.64, 0.5, 0.28, 0.3] },
  ],
  "demo-cam-front-gate": [
    { label: "Vehicles", count: 1, box: [0.22, 0.44, 0.34, 0.34] },
    { label: "People", count: 2, box: [0.63, 0.4, 0.12, 0.4] },
  ],
  "demo-cam-lobby": [
    { label: "People", count: 6, box: [0.2, 0.36, 0.56, 0.46] },
  ],
  "demo-cam-parking": [
    { label: "Vehicles", count: 9, box: [0.1, 0.46, 0.76, 0.32] },
  ],
  "demo-cam-perimeter": [],
};

export const demoDetections = (
  cameraId: string,
  tick: number
): BeamDetection[] =>
  (DEMO_DETECTIONS[cameraId] || []).map((detection, index) => {
    const drift = wobble(index * 5, tick, 0.02);
    const [x, y, width, height] = detection.box;
    return {
      label: detection.label,
      count: detection.count,
      box: [
        Math.max(0, Math.min(1 - width, x + drift)),
        Math.max(0, Math.min(1 - height, y + drift * 0.4)),
        width,
        height,
      ] as [number, number, number, number],
      confidence: 0.86 + wobble(index * 3, tick, 0.05),
    };
  });

/* ---------------- beamRx sensors ---------------- */

export const DEMO_SENSOR_DEVICES: BeamSensorDevice[] = [
  {
    id: "demo-rx-wearable",
    name: "Wearable · Unit 7",
    deviceKey: "demo-rx-wearable",
    addedAt: moment().subtract(4, "days").toISOString(),
  },
  {
    id: "demo-rx-room",
    name: "Room Monitor · B",
    deviceKey: "demo-rx-room",
    addedAt: moment().subtract(4, "days").toISOString(),
  },
];

interface DemoMetric {
  key: string;
  label: string;
  base: number;
  swing: number;
  unit?: string;
  decimals: number;
}

const DEMO_METRICS: Record<string, DemoMetric[]> = {
  "demo-rx-wearable": [
    { key: "heartRate", label: "Heart rate", base: 74, swing: 7, unit: "bpm", decimals: 0 },
    { key: "bodyTemp", label: "Body temperature", base: 36.8, swing: 0.3, unit: "°C", decimals: 1 },
    { key: "spo2", label: "Blood oxygen", base: 97, swing: 1.4, unit: "%", decimals: 0 },
    { key: "systolic", label: "Blood pressure (systolic)", base: 121, swing: 6, unit: "mmHg", decimals: 0 },
    { key: "respiration", label: "Respiration rate", base: 16, swing: 2, unit: "br/min", decimals: 0 },
    { key: "skinTemp", label: "Skin temperature", base: 33.1, swing: 0.4, unit: "°C", decimals: 1 },
  ],
  "demo-rx-room": [
    { key: "ambientTemp", label: "Ambient temperature", base: 22.4, swing: 0.6, unit: "°C", decimals: 1 },
    { key: "humidity", label: "Humidity", base: 44, swing: 4, unit: "%", decimals: 0 },
    { key: "co2", label: "Carbon dioxide", base: 610, swing: 70, unit: "ppm", decimals: 0 },
    { key: "noise", label: "Sound level", base: 41, swing: 6, unit: "dB", decimals: 0 },
  ],
};

export const demoSensorReadings = (
  deviceKey: string,
  tick: number
): BeamSensorReading[] => {
  const metrics = DEMO_METRICS[deviceKey] || [];
  const timestamp = Date.now();
  return metrics.map((metric, index) => ({
    key: metric.key,
    label: metric.label,
    value: Number(
      (metric.base + wobble(index * 11, tick, metric.swing)).toFixed(
        metric.decimals
      )
    ),
    unit: metric.unit,
    timestamp,
  }));
};
